import { toCzkMinor } from './money.js';
import { rankOffers } from './promotions.js';
import { inBasket } from './totals.js';
/**
 * Zmena nekoupene polozky na akci: presun do obchodu s akci, cena z akce,
 * nazev z letaku do prazdne poznamky a nenalezena zpet mezi nekoupene.
 */
export function offerPatch(open, offer) {
    const patch = {};
    const moving = offer.storeId !== null && open.storeId !== offer.storeId;
    if (moving)
        patch.storeId = offer.storeId;
    if (open.status === 'not_found')
        patch.status = 'pending';
    if (offer.priceMinor !== null) {
        patch.expectedPriceMinor = offer.priceMinor;
        patch.expectedCurrency = offer.currency;
    }
    else if (moving && open.expectedCurrency !== offer.currency) {
        // Cena v zlotych v Kauflandu nedava smysl; akce bez ceny ji nenahradi.
        patch.expectedPriceMinor = null;
        patch.expectedCurrency = offer.currency;
    }
    if (!open.note)
        patch.note = offer.name.slice(0, 500);
    // Akce bez ceny („druhý za 1 zł", 31 % Biedronky) se pamatuje taky — cena polozky zustava,
    // ale je videt, ze je v akci a do kdy (Plátkový sýr, 2026-10-13).
    if (offer.offer) {
        patch.deal = {
            offerId: offer.offer.id,
            name: offer.name.slice(0, 500),
            validTo: offer.offer.validTo,
            requiresCard: offer.offer.requiresCard,
            // Druha sleva na teze polozce si drzi stav pred prvni.
            prev: open.deal?.prev ?? { priceMinor: open.expectedPriceMinor, currency: open.expectedCurrency, storeId: open.storeId },
        };
    }
    else if (open.deal) {
        patch.deal = null;
    }
    return patch;
}
/** „Zrušit akci": puvodni cena a obchod, nazev z letaku zmizi z poznamky. */
export function dealUndoPatch(item) {
    const deal = item.deal;
    if (!deal)
        return {};
    const patch = { deal: null };
    if (deal.prev.priceMinor !== item.expectedPriceMinor || deal.prev.currency !== item.expectedCurrency) {
        patch.expectedPriceMinor = deal.prev.priceMinor;
        patch.expectedCurrency = deal.prev.currency;
    }
    if (deal.prev.storeId !== item.storeId)
        patch.storeId = deal.prev.storeId;
    if (item.note === deal.name)
        patch.note = null;
    return patch;
}
/**
 * Presun polozky do jineho obchodu rukou. Cena v jine mene nedava smysl a cena
 * z akce plati jen v obchode akce — vrati se cena pred slevou (je-li ve stejne mene).
 */
export function moveItemPatch(item, target) {
    const patch = { storeId: target.id };
    if (item.deal) {
        const prev = item.deal.prev;
        patch.deal = null;
        patch.expectedPriceMinor = prev.currency === target.currency ? prev.priceMinor : null;
        patch.expectedCurrency = target.currency;
        if (item.note === item.deal.name)
            patch.note = null;
    }
    else if (item.expectedCurrency !== target.currency) {
        patch.expectedPriceMinor = null;
        patch.expectedCurrency = target.currency;
    }
    return patch;
}
/** Akce skonci pred dnem nakupu (treba po posunuti data) — cena z ni v obchode nebude. */
export function dealEndsBefore(deal, plannedDate) {
    if (!deal.validTo || !plannedDate)
        return false;
    return Date.parse(deal.validTo) < Date.parse(`${plannedDate}T00:00:00`);
}
/**
 * Akce polozky sablony na nakup. Kdyz tam polozka ze stejne sablony uz je,
 * nepribude druha: nekoupena se presune do obchodu s akci (i z jineho obchodu)
 * a vezme si cenu z akce, koupena zustane, jak je.
 */
export function planOfferAdd(items, offer) {
    if (!offer.catalogItemId)
        return { kind: 'add' };
    const same = items.filter((i) => i.catalogItemId === offer.catalogItemId);
    const open = same.find((i) => !inBasket(i));
    if (!open) {
        const bought = same[0];
        return bought ? { kind: 'in_basket', item: bought } : { kind: 'add' };
    }
    return { kind: 'update', item: open, patch: offerPatch(open, offer) };
}
/**
 * Navrhy slev pro kontrolu. Predvybira se jen jiste: polozka ze sablony, akce
 * ve stejnem obchode a levnejsi nez dosavadni cena (nebo polozka cenu nemela).
 * Presun do jineho obchodu a shoda jen podle nazvu ceka na cloveka.
 */
export function planTripDeals(items, deals, fx) {
    const byItem = new Map(deals.map((deal) => [deal.tripItemId, deal]));
    const proposals = [];
    for (const item of items) {
        const deal = byItem.get(item.id);
        if (!deal || inBasket(item))
            continue;
        const oldCzk = item.expectedPriceMinor !== null && item.expectedCurrency !== null
            ? toCzkMinor(item.expectedPriceMinor * item.qty, item.expectedCurrency, fx)
            : null;
        const options = [];
        const seen = new Set();
        for (const { offer, storeIds } of deal.candidates) {
            if (seen.has(offer.id))
                continue;
            seen.add(offer.id);
            const storeId = storeIds.length === 0 ? item.storeId : storeIds.includes(item.storeId ?? '') ? item.storeId : (storeIds[0] ?? null);
            const moves = storeId !== item.storeId;
            // Tahle cena uz u polozky je — nabizet ji znovu nema smysl.
            if (!moves && item.deal?.offerId === offer.id)
                continue;
            if (!moves &&
                offer.priceMinor !== null &&
                offer.priceMinor === item.expectedPriceMinor &&
                offer.currency === item.expectedCurrency)
                continue;
            const newCzk = offer.priceMinor === null ? null : toCzkMinor(offer.priceMinor * item.qty, offer.currency, fx);
            const savingCzkMinor = oldCzk === null || newCzk === null ? null : Math.round(oldCzk - newCzk);
            options.push({
                offer,
                storeId,
                moves,
                patch: offerPatch(item, { storeId, priceMinor: offer.priceMinor, currency: offer.currency, name: offer.name, offer }),
                savingCzkMinor,
            });
        }
        if (options.length === 0)
            continue;
        const ranked = rankOffers(options.map((option) => ({ ...option, priceMinor: option.offer.priceMinor, currency: option.offer.currency })), fx);
        const sorted = [...ranked.filter((o) => !o.moves), ...ranked.filter((o) => o.moves)].map(({ offer, storeId, moves, patch, savingCzkMinor }) => ({ offer, storeId, moves, patch, savingCzkMinor }));
        const sameCurrency = (option) => item.expectedCurrency === null || option.offer.currency === item.expectedCurrency;
        const index = sorted.findIndex((option) => !option.moves &&
            deal.by === 'catalog' &&
            // Bez ceny nejde rict, jestli se vyplati — rozhodne clovek.
            option.offer.priceMinor !== null &&
            (item.expectedPriceMinor === null ? sameCurrency(option) : (option.savingCzkMinor ?? 0) > 0));
        proposals.push({ item, by: deal.by, options: sorted, preselected: index >= 0 ? index : null });
    }
    return proposals;
}
/** Zmena, ktera vrati polozce puvodni hodnoty poli, ktera patch meni. */
export function revertPatch(item, patch) {
    const revert = {};
    for (const key of Object.keys(patch))
        revert[key] = item[key];
    return revert;
}
export function tripItemDealState(item, proposal, plannedDate) {
    if (item.deal)
        return { kind: 'applied', deal: item.deal, ended: dealEndsBefore(item.deal, plannedDate) };
    if (!proposal || inBasket(item))
        return null;
    const here = proposal.options.filter((option) => !option.moves);
    const first = here[0];
    if (first)
        return { kind: 'here', option: first, count: here.length };
    const away = proposal.options[0];
    if (!away)
        return null;
    return { kind: 'elsewhere', option: away, cheaper: (away.savingCzkMinor ?? 0) > 0 };
}
/** Pruh nad seznamem: kolik polozek je v akci a kolik usetri predvybrane slevy. */
export function dealsSummary(proposals) {
    let savingCzkMinor = 0;
    for (const proposal of proposals) {
        const option = proposal.preselected === null ? undefined : proposal.options[proposal.preselected];
        savingCzkMinor += Math.max(0, option?.savingCzkMinor ?? 0);
    }
    return { count: proposals.length, savingCzkMinor };
}
//# sourceMappingURL=offerTrip.js.map